import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CloudUpload, FolderOpen, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useProject } from "@/lib/workflow/useProject";
import { parseProjectFile, serializeProject } from "@/lib/workflow/projectFile";

const fmtSize = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);

/**
 * Event Suite (Standard and Premium): projects saved to the account, up to 1 GB. Shown to subscribers. The project
 * file goes straight to our storage through a short-lived link; opening one loads it into this tab.
 */
export function AccountProjects() {
  const [project, store] = useProject();
  const utils = trpc.useUtils();
  const list = trpc.eventSuite.storage.list.useQuery(undefined, { retry: false });
  const prepare = trpc.eventSuite.storage.prepareSave.useMutation();
  const confirm = trpc.eventSuite.storage.confirmSave.useMutation();
  const openMut = trpc.eventSuite.storage.open.useMutation();
  const removeMut = trpc.eventSuite.storage.remove.useMutation();
  const [busy, setBusy] = useState<string | null>(null);
  // the saved copy this tab's project came from or was last saved as, so Save updates it instead of adding another
  const [linked, setLinked] = useState<string | null>(null);

  const save = async (asNew: boolean) => {
    setBusy("save");
    try {
      const body = serializeProject(project);
      const blob = new Blob([body], { type: "application/json" });
      const p = await prepare.mutateAsync({ projectId: asNew ? undefined : linked ?? undefined, name: project.name, sizeBytes: blob.size });
      const put = await fetch(p.uploadUrl, { method: "PUT", headers: { "Content-Type": "application/json" }, body: blob });
      if (!put.ok) throw new Error("The upload did not finish. Please try again.");
      await confirm.mutateAsync({ projectId: p.projectId, name: project.name });
      setLinked(p.projectId);
      await utils.eventSuite.storage.list.invalidate();
      toast.success("Saved to your account");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const open = async (id: string) => {
    if (!window.confirm("Open this saved project? The one open now is replaced unless you saved it.")) return;
    setBusy(id);
    try {
      const r = await openMut.mutateAsync({ projectId: id });
      const parsed = parseProjectFile(r.json);
      if (!parsed.ok) throw new Error(parsed.error);
      store.reset(parsed.project);
      setLinked(id);
      toast.success(`Opened ${r.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open that project.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}" from your account? This cannot be undone.`)) return;
    setBusy(id);
    try {
      await removeMut.mutateAsync({ projectId: id });
      if (linked === id) setLinked(null);
      await utils.eventSuite.storage.list.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete that project.");
    } finally {
      setBusy(null);
    }
  };

  if (list.isError) return null;
  const d = list.data;
  const pct = d ? Math.min(100, Math.round((d.usedBytes / d.limitBytes) * 100)) : 0;

  return (
    <section className="mt-3 rounded-lg border bg-card p-4 text-sm" data-testid="account-projects">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">Saved to your account</h3>
        {d && <span className="text-xs text-muted-foreground">{fmtSize(d.usedBytes)} of {fmtSize(d.limitBytes)} used</span>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => void save(false)} disabled={busy !== null} data-testid="account-save">
            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5 mr-1" />}
            {linked ? "Save changes" : "Save to account"}
          </Button>
          {linked && <Button size="sm" variant="outline" onClick={() => void save(true)} disabled={busy !== null}>Save as new copy</Button>}
        </div>
      </div>
      {d && <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-muted" aria-hidden><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>}
      {list.isLoading && <p className="mt-3 text-xs text-muted-foreground">Loading your saved projects</p>}
      {d && d.projects.length === 0 && <p className="mt-3 text-xs text-muted-foreground">Nothing saved yet. Save this project to open it from any device.</p>}
      {d && d.projects.length > 0 && (
        <ul className="mt-3 divide-y">
          {d.projects.map((p) => (
            <li key={p.id} className="flex items-center gap-2 py-2">
              <span className="min-w-0 flex-1 truncate">
                <strong>{p.name}</strong>
                <span className="ml-2 text-xs text-muted-foreground">{fmtSize(p.sizeBytes)}, {new Date(p.updatedAt).toLocaleDateString()}</span>
              </span>
              <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void open(p.id)}>
                {busy === p.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FolderOpen className="h-3.5 w-3.5 mr-1" />}Open
              </Button>
              <Button size="sm" variant="ghost" aria-label={`Delete ${p.name}`} disabled={busy !== null} onClick={() => void remove(p.id, p.name)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Kept for as long as your Event Suite plan runs. If it ends, saved projects are erased, so download a copy first with Save project.</p>
    </section>
  );
}
